import { create } from "zustand";
import {
  fetchMyAccounts,
  mintAccountConnect,
  VybeAuthExpiredError,
  type SyncedAccount,
  type SyncedAccountGateway,
} from "@/lib/vybe-auth/api";
import { getVybeToken } from "@/lib/vybe-auth/store";
import { useAccountsStore } from "./store";
import { decodeConnectToken, connectPayloadToAccount } from "./connectLink";

/**
 * Internal account sync: the trading accounts the signed-in user's CRM profile owns, shown in the
 * terminal's Trading Accounts panel alongside the ones they added by hand.
 *
 * STATE LIVES ONLY IN MEMORY — deliberately, not as an oversight. These rows are a projection of a live
 * session's entitlement, so persisting them would let a signed-out browser keep showing accounts we no
 * longer have the right to list. Sign-out (and an expired token) drops them; a reload re-fetches them if
 * and only if a session is restored. Locally-added accounts are untouched by all of this — they live in
 * `useAccountsStore` and its localStorage, and they are the user's own, not the profile's.
 *
 * A synced row is a DESCRIPTION, not a credential: it carries no password. Connecting one either selects
 * the matching local account (using the password already on this device) or asks the CRM to mint a
 * one-account connect token, which materialises it as a real local row marked `origin: "vybe"` so
 * sign-out can purge exactly what the session brought in.
 */

export interface SyncedAccountView extends SyncedAccount {
  gateway: SyncedAccountGateway;
}

export type SyncedAccountsStatus = "idle" | "loading" | "ready" | "unavailable";

interface SyncedAccountsStore {
  items: SyncedAccountView[];
  status: SyncedAccountsStatus;
  /** Why the sync is unavailable, when it is. Never a reason to hide local accounts. */
  error: string | null;
  /** Login currently being prepared for connection (the mint round-trip), if any. */
  connectingLogin: number | null;
  load: () => Promise<void>;
  /**
   * Re-fetch the list even when a fetch already ran — the entry point for every freshness signal
   * (presence "VybeOnAccountsChanged" push, panel open, window focus). Coalesces with an in-flight
   * fetch by queueing exactly one follow-up, so a burst of signals costs at most two requests.
   */
  refresh: () => Promise<void>;
  clear: () => void;
  connect: (login: number) => Promise<void>;
}

/** Guards against the panel and the sign-in effect both firing a fetch on the same tick. */
let inFlight: Promise<void> | null = null;
/** One queued follow-up for refresh() calls that landed while a fetch was mid-flight. */
let followUpQueued = false;

/**
 * Reconcile the locally materialised rows with the profile's list (the "removals sync"): a local
 * account the SESSION brought in (`origin: "vybe"`) whose login the CRM no longer lists has been
 * removed from the profile, so it goes here too — including disconnecting it if it was active
 * (deleteAccount clears the active id, which is what tears the hub connection down). Hand-added
 * rows (no `origin`) are never touched: they are the user's own, not the profile's.
 */
function purgeRemovedVybeRows(listed: SyncedAccount[]): void {
  const listedLogins = new Set(listed.map((a) => String(a.login)));
  const store = useAccountsStore.getState();
  for (const account of store.accounts) {
    if (account.origin !== "vybe") continue;
    if (listedLogins.has(account.credentials.login)) continue;
    useAccountsStore.getState().deleteAccount(account.id);
  }
}

export const useSyncedAccountsStore = create<SyncedAccountsStore>((set, get) => ({
  items: [],
  status: "idle",
  error: null,
  connectingLogin: null,

  load: async () => {
    const token = getVybeToken();
    // No session ⇒ no entitlement to show anything. Also the reset path for a sign-out that raced a load.
    if (!token) {
      get().clear();
      return;
    }
    if (inFlight) return inFlight;

    set({ status: "loading", error: null });
    inFlight = (async () => {
      try {
        const res = await fetchMyAccounts(token);
        if (!res.ok || !res.gateway) {
          // The CRM answered, but its trading server didn't. Say so; keep local accounts intact.
          set({ items: [], status: "unavailable", error: res.error ?? "Account sync is unavailable." });
          return;
        }
        const gateway = res.gateway;
        set({
          items: res.accounts.map((a) => ({ ...a, gateway })),
          status: "ready",
          error: null,
        });
        // Only on an OK answer: an unreachable CRM must never delete a device's rows.
        purgeRemovedVybeRows(res.accounts);
      } catch (e) {
        // An expired session is not a failed fetch: we've lost the right to list these, so drop them —
        // and the rows the session materialised too, exactly as a deliberate sign-out would.
        if (e instanceof VybeAuthExpiredError) {
          set({ items: [], status: "idle", error: null });
          useAccountsStore.getState().purgeSyncedAccounts();
          return;
        }
        set({
          items: [],
          status: "unavailable",
          error: e instanceof Error ? e.message : "Account sync is unavailable.",
        });
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  },

  refresh: async () => {
    if (inFlight) {
      // Ride out the current fetch, then run exactly one more — it may predate the change that
      // triggered this signal. Later refreshes during the wait collapse into the same follow-up.
      if (followUpQueued) return inFlight;
      followUpQueued = true;
      try {
        await inFlight;
      } finally {
        followUpQueued = false;
      }
    }
    return get().load();
  },

  clear: () => {
    inFlight = null;
    followUpQueued = false;
    set({ items: [], status: "idle", error: null, connectingLogin: null });
  },

  connect: async (login: number) => {
    const store = useAccountsStore.getState();

    // Already on this device → connect with the password the user already has. This is the path that keeps
    // the CRM from rotating a working credential, so it must be tried FIRST.
    const local = findLocalByLogin(store.accounts, login);
    if (local?.credentials.password) {
      store.setActiveAccountId(local.id);
      return;
    }

    const token = getVybeToken();
    if (!token) throw new Error("Sign in to connect this account");

    set({ connectingLogin: login });
    try {
      const payload = decodeConnectToken(await mintAccountConnect(token, login));
      if (!payload) throw new Error("The server returned unusable connection details");

      // Re-read: the user may have added or edited this account while we were minting.
      const current = useAccountsStore.getState();
      const existing = findLocalByLogin(current.accounts, login);
      if (existing) {
        // Adopt the fresh credential onto the row that's already there rather than adding a duplicate.
        current.updateAccount({
          ...existing,
          credentials: { ...existing.credentials, password: payload.password },
          origin: existing.origin ?? "vybe",
        });
        current.setActiveAccountId(existing.id);
      } else {
        current.addAccount({ ...connectPayloadToAccount(payload), origin: "vybe" });
      }
      // Imported lazily: hooks.ts binds this store to the session, so a static import would close a cycle.
      void import("./hooks").then((m) => m.backfillBrokerLogos());
    } catch (e) {
      if (e instanceof VybeAuthExpiredError) {
        get().clear();
        useAccountsStore.getState().purgeSyncedAccounts();
      }
      throw e;
    } finally {
      set({ connectingLogin: null });
    }
  },
}));

/**
 * Replace a Vybe account's stored password with a freshly minted one, for a row whose saved credential the
 * gateway has just rejected. Returns the new password, or null when we can't mint (no session, not an account
 * this profile owns, CRM unreachable) — the caller then reports the original auth failure.
 *
 * This is the recovery path for a rotated credential. The CRM cannot read an existing password (the engine
 * stores only an argon2id digest), so re-minting — which rotates — is the ONLY way it can hand over a working
 * one. That makes this deliberately narrow: it runs after an authentication failure, never speculatively,
 * because each call invalidates whatever that account's password was on the user's other devices.
 */
export async function refreshVybeCredential(login: number): Promise<string | null> {
  const token = getVybeToken();
  if (!token) return null;

  try {
    const payload = decodeConnectToken(await mintAccountConnect(token, login));
    if (!payload?.password) return null;

    const store = useAccountsStore.getState();
    const local = findLocalByLogin(store.accounts, login);
    if (!local) return null;

    store.updateAccount({
      ...local,
      credentials: { ...local.credentials, password: payload.password },
      origin: local.origin ?? "vybe",
    });
    return payload.password;
  } catch (e) {
    if (e instanceof VybeAuthExpiredError) {
      useSyncedAccountsStore.getState().clear();
      useAccountsStore.getState().purgeSyncedAccounts();
    }
    return null;
  }
}

/**
 * The local row for a login, if any. Matched on LOGIN ALONE for Vybe-gateway accounts, for the same reason
 * `useConnectDeepLink` does: one trading account can be fronted by different gateway hosts over time, and a
 * host-sensitive match would leave the trader looking at two rows for one account.
 */
function findLocalByLogin<T extends { credentials: { login: string }; providerKind?: string }>(
  accounts: T[],
  login: number,
): T | undefined {
  const asString = String(login);
  return accounts.find((a) => a.credentials.login === asString);
}

/**
 * A synced account that is NOT already present locally — what the panel renders as a badged, read-only row.
 * Deduped by login, so an account the user happens to have added by hand shows once, as their own row (with
 * its edit/delete affordances intact), not twice.
 */
export function selectUnmatchedSynced(
  items: SyncedAccountView[],
  localLogins: Set<string>,
): SyncedAccountView[] {
  return items.filter((a) => !localLogins.has(String(a.login)));
}
